import { ChangeDetectionStrategy, Component } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { LicenseOverrideType } from '../../../../shared/license-list/models/license-override-list.model';
import { GroupIdHelper } from '../../../../shared/helpers/group-id.helper';
import { LicenseService } from '../../../../shared/license-list/services/license.service';
import { LocalSettingsService } from '../../../../../lib/services/local-settings.service';

interface GroupOverrideOption {
  readonly value: Exclude<LicenseOverrideType, 'IN_ORDER'>;
  readonly label: string;
}

@Component({
  selector: 'ms-group-override-dialog',
  templateUrl: './group-override-dialog.component.html',
  styleUrls: ['./group-override-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class GroupOverrideDialogComponent {
  public readonly overrideTypes: GroupOverrideOption[] = [
    { value: 'READ_ONLY', label: 'Lecture seule' },
    { value: 'GRACE_PERIOD', label: 'Avertissement' },
    { value: 'NOT_VALID', label: 'Bloqué' },
  ];

  public readonly form: FormGroup<{
    groupId: FormControl<string>;
    overrideType: FormControl<Exclude<LicenseOverrideType, 'IN_ORDER'> | null>;
    reason: FormControl<string>;
  }> = new FormGroup({
    groupId: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required, this.validateGroupId],
    }),
    overrideType: new FormControl<Exclude<LicenseOverrideType, 'IN_ORDER'> | null>(
      null,
      {
        validators: [Validators.required],
      },
    ),
    reason: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required, Validators.minLength(3)],
    }),
  });

  constructor(
    private dialogRef: MatDialogRef<GroupOverrideDialogComponent>,
    private licenseService: LicenseService,
    private localSettingsService: LocalSettingsService,
  ) {}

  public lockGroupId(groupId: string): void {
    this.groupIdControl.setValue(groupId);
    this.groupIdControl.disable();
  }

  public get isGroupIdReadOnly(): boolean {
    return this.groupIdControl.disabled;
  }

  public get canSubmit(): boolean {
    return this.form.valid;
  }

  public get groupIdControl(): FormControl<string> {
    return this.form.controls.groupId;
  }

  public get reasonControl(): FormControl<string> {
    return this.form.controls.reason;
  }

  public get overrideTypeControl(): FormControl<Exclude<
    LicenseOverrideType,
    'IN_ORDER'
  > | null> {
    return this.form.controls.overrideType;
  }

  public get hasGroupIdError(): boolean {
    return this.groupIdControl.invalid && (this.groupIdControl.dirty || this.groupIdControl.touched);
  }

  public get hasInvalidGroupIdError(): boolean {
    return this.groupIdControl.hasError('invalidGroupId');
  }

  public get hasReasonError(): boolean {
    return this.reasonControl.invalid && (this.reasonControl.dirty || this.reasonControl.touched);
  }

  public get hasOverrideTypeError(): boolean {
    return this.overrideTypeControl.invalid && (this.overrideTypeControl.dirty || this.overrideTypeControl.touched);
  }

  public closeDialog(): void {
    this.dialogRef.close();
  }

  public async addGroupOverride(): Promise<void> {
    this.form.markAllAsTouched();

    if (!this.form.valid) {
      return;
    }

    const token: string | null = this.localSettingsService.oauthIdToken;

    if (!token) {
      return;
    }

    await this.licenseService.createGroupOverride(
      token,
      this.groupIdControl.value,
      this.overrideTypeControl.value as Exclude<LicenseOverrideType, 'IN_ORDER'>,
      this.reasonControl.value,
    );

    this.dialogRef.close({ created: true });
  }

  private validateGroupId(
    control: AbstractControl<string>,
  ): ValidationErrors | null {
    if (!GroupIdHelper.hasUuidSuffix(control.value)) {
      return { invalidGroupId: true };
    }

    return null;
  }
}
